import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { Flex } from '../Flex.js';
import { Kbd } from '../Kbd.js';
import { preventForwardPropsConfig } from '../../utils/preventForwardProps.js';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { keyframes, styled, css } from 'styled-components';
import { getCssBackground } from '../../theme/cssBackground.js';
import { cssVar } from '../../theme/variables.js';
import { Button } from '../Button.js';
import { mediaMobile, mediaNotMobile } from '../../constants/styled-utils.js';
import { getRadixModalModeEnabled } from '@fabric-app/compat';
import { handleViewportCSS } from '../../hooks/useVisualViewport.js';
import { IconNavigational24Cross, IconNavigational24BackArrow } from '@fabric-app/icons/svg';
import { useResponsive } from '../../hooks/useResponsive.js';

const fadeIn = keyframes([
  `from{opacity:0;}to{opacity:1;}`
]);
const fadeOut = keyframes([
  `from{opacity:1;}to{opacity:0;}`
]);
const scaleIn = keyframes([
  `from{transform:translate3d(var(--modal-transform-x),var(--modal-transform-y),0) scale(0.95);}to{transform:translate3d(var(--modal-transform-x),var(--modal-transform-y),0) scale(1);}`
]);
const scaleOut = keyframes([
  `from{transform:translate3d(var(--modal-transform-x),var(--modal-transform-y),0) scale(1);}to{transform:translate3d(var(--modal-transform-x),var(--modal-transform-y),0) scale(0.95);}`
]);
const ModalRoot = styled(DialogPrimitive.Root).attrs((props) => {
  return {
    modal: getRadixModalModeEnabled(),
    ...props
  };
}).withConfig({
  displayName: "Modal__ModalRoot",
  componentId: "sc-5ff7ff98-0"
})([
  ``
]);
const ModalTrigger = DialogPrimitive.Trigger;
const ModalPortal = DialogPrimitive.Portal;
const ModalOverlay = styled.div.attrs((props) => {
  return {
    as: getRadixModalModeEnabled() ? DialogPrimitive.Overlay : void 0,
    ...props
  };
}).withConfig(preventForwardPropsConfig([
  "visibleOnMobileViewport",
  "noMobileBlur"
])).withConfig({
  displayName: "Modal__ModalOverlay",
  componentId: "sc-5ff7ff98-1"
})([
  `position:fixed;inset:0;z-index:125;background-color:`,
  `;will-change:opacity;&[data-state='closed']{animation:`,
  ` 0.2s ease-out;}`,
  `{`,
  `;`,
  `;}`
], cssVar["color-bg-overlay"], fadeOut, mediaMobile, (p) => !p.visibleOnMobileViewport && css([
  `display:none;`
]), (p) => p.noMobileBlur && css([
  `backdrop-filter:none;-webkit-backdrop-filter:none;background-color:`,
  `;`
], cssVar["color-bg-quinary"]));
const modalWidths = {
  xs: "400px",
  ["xs+"]: "420px",
  sm: "450px",
  ["sm+"]: "500px",
  ["md-"]: "590px",
  md: "640px",
  lg: "830px",
  full: "100%"
};
const modalHeights = {
  sm: "50vh",
  md: "70vh",
  lg: "90vh",
  full: "100%"
};
const modalContentGaps = {
  none: "0",
  xs: "5px",
  sm: "10px",
  md: "20px",
  lg: "30px"
};
const defaultModalContentProps = {
  onOpenAutoFocus: (e) => {
    e.preventDefault();
  },
  onCloseAutoFocus: (e) => {
    e.preventDefault();
  },
  "aria-label": "modal"
};
const preventForwardedItems = [
  "respectMobileKeyboard",
  "dynamicHeight",
  "disableMobileFullscreen"
];
const ModalContent = styled(DialogPrimitive.Content).attrs((props) => {
  return {
    ...defaultModalContentProps,
    ...props
  };
}).withConfig(preventForwardPropsConfig(preventForwardedItems)).withConfig({
  displayName: "Modal__ModalContent",
  componentId: "sc-5ff7ff98-2"
})([
  `position:fixed;max-width:640px;z-index:125;display:flex;flex-direction:column;min-width:0;min-height:0;border:1px solid `,
  `;background:`,
  `;padding-top:env(safe-area-inset-top);padding-bottom:env(safe-area-inset-bottom);transition:all 0.15s ease;transition-delay:0;will-change:transform,opacity,height;height:max-content;max-height:90vh;overflow:hidden;outline:none !important;justify-content:`,
  `;align-items:`,
  `;--modal-transform-x:calc(-50% - 0.05px);--modal-transform-y:-50%;left:50%;top:calc(50% - var(--keyboard-height,0px) / 3);transform:translate(var(--modal-transform-x),var(--modal-transform-y));border-radius:`,
  `px;box-shadow:0px 4px 100px 0px rgba(var(--fabric-color-bg-primary-reverse-rgb),0.05);&[data-state='open']{animation:`,
  ` 0.2s ease-out,`,
  ` 0.2s ease-out;}&[data-state='closed']{animation:`,
  ` 0.2s ease-out,`,
  ` 0.2s ease-out;}`,
  ` `,
  ` `,
  ``
], cssVar["color-border-primary"], cssVar["color-bg-primary"], ({ justifyContent }) => justifyContent ?? "initial", ({ alignItems }) => alignItems ?? "initial", ({ width, height }) => width === "full" && height === "full" ? 0 : 20, fadeIn, scaleIn, fadeOut, scaleOut, (p) => !p.disableMobileFullscreen ? css([
  ``,
  `{top:0;left:0;right:0;bottom:0;transform:none;border-radius:0;box-shadow:none;height:100%;max-height:100%;--modal-transform-x:0;--modal-transform-y:0;}`
], mediaMobile) : css([
  `padding-top:0;padding-bottom:0;`
]), ({ width, height, gap, dynamicWidth = false, dynamicHeight = true, disableMobileFullscreen }) => css([
  `max-width:`,
  `;max-height:`,
  `;gap:`,
  `;width:`,
  `;height:`,
  `;`,
  ``
], modalWidths[width ?? "md"], modalHeights[height ?? "md"], modalContentGaps[gap ?? "xs"], dynamicWidth ? "max-content" : "100%", dynamicHeight ? "max-content" : "100%", !disableMobileFullscreen ? css([
  ``,
  `{max-width:100%;max-height:100%;width:100%;height:100%;}`
], mediaMobile) : css([
  ``,
  `{max-width:calc(100dvw - 2rem);border-radius:2rem;}`
], mediaMobile)), (p) => !p.disableMobileFullscreen && p.respectMobileKeyboard && css([
  `:not(html[data-viewport='true']) &{`,
  `{height:calc(100% - var(--keyboard-height,0px));padding-bottom:calc( env(safe-area-inset-bottom) - clamp( 0px,env(safe-area-inset-bottom),var(--keyboard-height,0px) ) );}}`,
  ``
], mediaMobile, handleViewportCSS));
const ModalClose = styled(Button).attrs((props) => {
  return {
    variant: "bg-secondary",
    shape: "square",
    size: "default",
    autoFocus: false,
    as: DialogPrimitive.Close,
    ...props,
    children: /* @__PURE__ */ jsxs(Fragment, {
      children: [
        /* @__PURE__ */ jsx(IconNavigational24Cross, {}),
        /* @__PURE__ */ jsx("span", {
          className: "sr-only",
          children: "Close"
        })
      ]
    })
  };
}).withConfig(preventForwardPropsConfig([
  "noAutoMargin"
])).withConfig({
  displayName: "Modal__ModalClose",
  componentId: "sc-5ff7ff98-3"
})([
  `margin-left:`,
  `;`
], ({ noAutoMargin }) => noAutoMargin ? "unset" : "auto");
const ModalIconButton = styled(Button).attrs((props) => {
  return {
    variant: "bg-secondary",
    shape: "square",
    size: "default",
    autoFocus: false,
    ...props
  };
}).withConfig({
  displayName: "Modal__ModalIconButton",
  componentId: "sc-5ff7ff98-4"
})([
  `margin-left:auto;`
]);
const MobileBackButton = styled(Button).attrs((props) => {
  return {
    variant: "bg-secondary",
    size: "default",
    autoFocus: false,
    ...props,
    as: DialogPrimitive.Close,
    children: /* @__PURE__ */ jsx(IconNavigational24BackArrow, {})
  };
}).withConfig({
  displayName: "Modal__MobileBackButton",
  componentId: "sc-5ff7ff98-5"
})([
  `color:`,
  `;display:none;`,
  `{display:flex;}`
], cssVar["color-text-primary"], mediaMobile);
const cssModalBaseHeaderPadding = css([
  ``,
  `{`,
  `}`,
  `{padding:16px 20px;}`
], mediaNotMobile, (p) => {
  switch (p.padding) {
    case "sm":
      return css([
        `padding:13px 13px 13px 16px;`
      ]);
    case "default":
    default:
      return css([
        `padding:16px 20px;`
      ]);
  }
}, mediaMobile);
const cssModalBaseHeaderVariant = css([
  ``,
  ``
], (p) => {
  switch (p.variant) {
    case "simple":
      return css([
        `border-bottom:none;padding-bottom:0;`,
        `{padding-bottom:0;}`
      ], mediaNotMobile);
    case "default":
    default:
      return css([
        `border-bottom:1px solid `,
        `;`
      ], cssVar["color-border-primary"]);
  }
});
const ModalBaseHeader = styled.div.withConfig({
  displayName: "Modal__ModalBaseHeader",
  componentId: "sc-5ff7ff98-6"
})([
  `display:flex;gap:1.5rem;text-align:left;min-height:42px;align-items:center;flex-shrink:0;`,
  `{gap:0.625rem;}`,
  ` `,
  ` `,
  ` `,
  `{`,
  `}`
], mediaMobile, cssModalBaseHeaderPadding, cssModalBaseHeaderVariant, (p) => p.mobileShadowOverContent && css([
  ``,
  `{box-shadow:0px 4px 4px 0px rgba(`,
  `,0.02),0px 4px 25px 0px rgba(`,
  `,0.05);}`
], mediaMobile, cssVar["color-bg-primary-reverse-rgb"], cssVar["color-bg-primary-reverse-rgb"]), mediaNotMobile, (p) => p.mobileOnly && css([
  `display:none;`
]));
const ModalHeader = styled(ModalBaseHeader).attrs(({ hideCloseButtonOnMobile, titleProps, hideBackButtonOnMobile, ...props }) => {
  const { isMobileView } = useResponsive();
  return {
    ...props,
    children: /* @__PURE__ */ jsxs(Fragment, {
      children: [
        !hideBackButtonOnMobile && /* @__PURE__ */ jsx(MobileBackButton, {}),
        /* @__PURE__ */ jsx(ModalTitle, {
          ...titleProps,
          children: props.children
        }),
        isMobileView && hideCloseButtonOnMobile ? null : /* @__PURE__ */ jsx(ModalClose, {})
      ]
    })
  };
}).withConfig({
  displayName: "Modal__ModalHeader",
  componentId: "sc-5ff7ff98-7"
})([
  ``
]);
const cssModalFooterProperties = css([
  ``,
  ``
], (p) => {
  switch (p.variant) {
    case "call-to-action":
      return css([
        `justify-content:space-between;border-top:1px solid `,
        `;background-color:`,
        `;padding:16px 20px;`
      ], cssVar["color-border-primary"], cssVar["color-bg-secondary"]);
    case "fullscreen":
      return css([
        `justify-content:space-between;align-self:center;border-top:1px solid `,
        `;width:100%;max-width:350px;padding:`,
        `;`,
        `{padding:`,
        `;}`
      ], cssVar["color-border-primary"], p.padding === "sm" ? "10px 0 16px 0" : "20px 0 20px 0", mediaMobile, p.padding === "sm" ? "10px 13px 16px 13px" : "20px 20px 20px 20px");
    case "actions":
    default:
      return css([
        `justify-content:flex-end;padding:`,
        `;`
      ], p.padding === "sm" ? "0 13px 16px 13px" : "10px 20px 20px 20px");
  }
});
const ModalFooter = styled.div.withConfig({
  displayName: "Modal__ModalFooter",
  componentId: "sc-5ff7ff98-8"
})([
  `display:flex;flex-direction:row;align-items:center;gap:`,
  `;flex-grow:`,
  `;`,
  ` `,
  ``
], (p) => p.gap === "lg" ? "16px" : "10px", (p) => p.grow ? 1 : 0, cssModalFooterProperties, getCssBackground());
const cssModalTitleProperties = css([
  ``,
  ``
], (p) => {
  switch (p.size) {
    case "xs":
      return css([
        `font-size:16px;font-weight:700;`,
        `{font-size:20px;line-height:24px;min-height:0;font-weight:600;}`
      ], mediaMobile);
    case "sm":
      return css([
        `font-size:18px;font-weight:400;min-height:42px;`
      ]);
    case "sm-bold":
      return css([
        `font-size:18px;font-weight:600;min-height:42px;`,
        `{font-weight:500;}`
      ], mediaMobile);
    case "md-shared":
      return css([
        `font-size:20px;line-height:24px;min-height:0;font-weight:600;`
      ]);
    case "default":
    default:
      return css([
        `font-size:26px;font-weight:700;min-height:42px;`,
        `{font-size:20px;line-height:24px;min-height:0;font-weight:600;}`
      ], mediaMobile);
  }
});
const ModalTitle = styled(DialogPrimitive.Title).withConfig({
  displayName: "Modal__ModalTitle",
  componentId: "sc-5ff7ff98-9"
})([
  `color:`,
  `;font-style:normal;line-height:24px;display:inline-flex;align-items:center;`,
  ``
], cssVar["color-text-primary"], cssModalTitleProperties);
const ModalDescription = styled(DialogPrimitive.Description).withConfig({
  displayName: "Modal__ModalDescription",
  componentId: "sc-5ff7ff98-10"
})([
  `font-size:14px;font-weight:400;color:`,
  `;`
], cssVar["color-text-secondary"]);
const cssModalBodyPadding = css([
  ``,
  `{`,
  `}`,
  `{padding:`,
  `;}`
], mediaNotMobile, (p) => {
  switch (p.padding) {
    case "none":
      return css([
        `padding:0;`
      ]);
    case "sm":
      return css([
        `padding:16px;`
      ]);
    case "default":
    default:
      return css([
        `padding:20px;`,
        `{padding:24px;}`
      ], mediaMobile);
  }
}, mediaMobile, ({ padding }) => padding === "none" ? "0px" : "18px");
const ModalBody = styled.div.withConfig({
  displayName: "Modal__ModalBody",
  componentId: "sc-5ff7ff98-11"
})([
  `display:flex;flex-direction:column;min-height:0;padding:20px;gap:30px;color:`,
  `;flex-grow:`,
  `;`,
  ` `,
  ``
], cssVar["color-text-primary"], (p) => p.grow ? 1 : 0, cssModalBodyPadding, getCssBackground("primary"));
const ModalButton = styled(Button).attrs((props) => {
  return {
    variant: "primary",
    ...props,
    size: void 0
  };
}).withConfig({
  displayName: "Modal__ModalButton",
  componentId: "sc-5ff7ff98-12"
})([
  `font-weight:700;font-size:1rem;padding:0 0.625rem;gap:0.625rem;`,
  `{border-radius:16px;height:54px;font-weight:600;font-size:16px;padding-left:1.25rem;padding-right:1.25rem;padding:0 1.25rem;}`
], mediaMobile);
const ModalCancelButton = styled(ModalButton).attrs((props) => {
  return {
    ...props,
    variant: "bg-tertiary"
  };
}).withConfig({
  displayName: "Modal__ModalCancelButton",
  componentId: "sc-5ff7ff98-13"
})([
  ``
]);
const ModalDoubleButtonContainer = styled(Flex).attrs((props) => {
  return {
    gap: "elementsContainer",
    justifyContent: "flex-end",
    ...props
  };
}).withConfig({
  displayName: "Modal__ModalDoubleButtonContainer",
  componentId: "sc-5ff7ff98-14"
})([
  `width:100%;`,
  `{gap:0.625rem;& > *{flex-grow:1;width:1px;}}`
], mediaMobile);
const ModalFloatingDesktopCloseButton = styled(Button).attrs((props) => {
  return {
    variant: "bg-primary",
    autoFocus: false,
    as: DialogPrimitive.Close,
    ...props,
    children: /* @__PURE__ */ jsxs(Fragment, {
      children: [
        "Cancel ",
        /* @__PURE__ */ jsx(Kbd, {
          children: "Esc"
        })
      ]
    })
  };
}).withConfig({
  displayName: "Modal__ModalFloatingDesktopCloseButton",
  componentId: "sc-5ff7ff98-15"
})([
  `border-radius:20px;position:fixed;top:2rem;left:2rem;z-index:127;background:rgba(`,
  `);`,
  `{display:none;}`
], (cssVar["color-bg-primary-rgb"], 0.7), mediaMobile);
const Modal = {
  Root: ModalRoot,
  Trigger: ModalTrigger,
  Portal: ModalPortal,
  Overlay: ModalOverlay,
  Content: ModalContent,
  ContentRaw: DialogPrimitive.Content,
  Close: ModalClose,
  FloatingDesktopCloseButton: ModalFloatingDesktopCloseButton,
  Header: ModalHeader,
  BaseHeader: ModalBaseHeader,
  Footer: ModalFooter,
  Title: ModalTitle,
  Description: ModalDescription,
  Body: ModalBody,
  Button: ModalButton,
  IconButton: ModalIconButton,
  CancelButton: ModalCancelButton,
  DoubleButtonContainer: ModalDoubleButtonContainer
};

export { Modal, defaultModalContentProps };
//# sourceMappingURL=Modal.js.map
